import React, { useCallback, useEffect, useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { Bell, User, CalendarClock, Users, LogOut, Settings, Pill, LayoutGrid, Share2, ArrowRightLeft, CheckCheck, Building2, ClipboardList } from "lucide-react";
import { useAuth } from "@/lib/auth";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel,
  DropdownMenuSeparator, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { initials } from "@/lib/format";
import api from "@/lib/api";
import HealthCirclesLogo from "@/components/HealthCirclesLogo";
import NotificationCenter from "@/components/NotificationCenter";

const DOCTOR_NAV = [
  { to: "/dashboard", icon: LayoutGrid, label: "Dashboard", testId: "nav-dashboard" },
  { to: "/patients", icon: Users, label: "Patients", testId: "nav-patients" },
  { to: "/appointments", icon: CalendarClock, label: "Appointments", testId: "nav-appointments" },
  { to: "/journey", icon: ClipboardList, label: "Workflow", testId: "nav-journey" },
  { to: "/care-circle", icon: Share2, label: "Care Circle", testId: "nav-care-circle" },
  { to: "/referrals", icon: ArrowRightLeft, label: "Referrals", testId: "nav-referrals" },
];

const RECEPTIONIST_NAV = [
  { to: "/appointments", icon: CalendarClock, label: "Appointments", testId: "nav-appointments" },
  { to: "/patients", icon: Users, label: "Patients", testId: "nav-patients" },
  { to: "/journey", icon: ClipboardList, label: "Workflow", testId: "nav-journey" },
];

const NURSE_NAV = [
  { to: "/appointments", icon: CalendarClock, label: "Appointments", testId: "nav-appointments" },
  { to: "/journey", icon: ClipboardList, label: "Workflow", testId: "nav-journey" },
];

const MINIMAL_NAV = [
  { to: "/journey", icon: ClipboardList, label: "Workflow", testId: "nav-journey" },
];

function navForUser(user) {
  if (!user) return DOCTOR_NAV;
  if (user.role !== "staff") return DOCTOR_NAV;
  const roles = user.staff_roles || [];
  if (roles.includes("receptionist")) return RECEPTIONIST_NAV;
  if (roles.includes("nurse")) return NURSE_NAV;
  return MINIMAL_NAV;   // lab_tech / pharmacist / no-assignment
}

function timeAgo(iso) {
  if (!iso) return "";
  const now = Date.now();
  const d = new Date(iso).getTime();
  const s = Math.round((now - d) / 1000);
  if (s < 60) return "just now";
  if (s < 3600) return `${Math.round(s / 60)}m`;
  if (s < 86400) return `${Math.round(s / 3600)}h`;
  return `${Math.round(s / 86400)}d`;
}

function BellDropdown() {
  const [items, setItems] = useState([]);
  const [unread, setUnread] = useState(0);
  const [open, setOpen] = useState(false);
  const nav = useNavigate();

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/notifications", { params: { limit: 10 } });
      setItems(data.items || []);
      setUnread(Number(data.unread_count || 0));
    } catch { /* ignore */ }
  }, []);

  useEffect(() => {
    load();
    const t = setInterval(load, 60_000);   // refresh every minute
    return () => clearInterval(t);
  }, [load]);

  const handleClick = async (n) => {
    setOpen(false);
    if (!n.read_at) {
      try { await api.post(`/notifications/${n.id}/read`); } catch { /* ignore */ }
      setUnread((c) => Math.max(0, c - 1));
    }
    if (n.link_url) nav(n.link_url);
  };

  const markAll = async () => {
    try { await api.post("/notifications/read-all"); setUnread(0); load(); } catch { /* ignore */ }
  };

  return (
    <DropdownMenu open={open} onOpenChange={setOpen}>
      <DropdownMenuTrigger asChild>
        <button
          className="relative w-10 h-10 rounded-full hover:bg-[#F3F1EC] flex items-center justify-center text-[#47554D]"
          data-testid="notifications-btn"
          aria-label="Notifications"
        >
          <Bell size={18} strokeWidth={1.75} />
          {unread > 0 && (
            <span className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] rounded-full bg-[#B14A2A] text-white text-[10px] font-bold flex items-center justify-center px-1" data-testid="notifications-unread">
              {unread > 9 ? "9+" : unread}
            </span>
          )}
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="rounded-2xl w-80 p-0">
        <div className="flex items-center justify-between px-4 py-3 border-b border-[#EFECE5]">
          <div className="text-sm font-semibold text-[#1F2A24]" style={{ fontFamily: "Instrument Sans" }}>Notifications</div>
          {unread > 0 && (
            <button onClick={markAll} className="text-xs text-[#0A4C36] font-semibold inline-flex items-center gap-1 hover:underline" data-testid="notif-mark-all">
              <CheckCheck size={12} /> Mark all read
            </button>
          )}
        </div>
        <div className="max-h-80 overflow-y-auto">
          {items.length === 0 && (
            <div className="text-xs text-[#8A9992] py-10 text-center">You're all caught up.</div>
          )}
          {items.map((n) => (
            <button
              key={n.id}
              onClick={() => handleClick(n)}
              data-testid={`notif-${n.id}`}
              className={`w-full text-left px-4 py-3 border-b border-[#F3F1EC] hover:bg-[#F7F4EC]/60 flex gap-3 ${n.read_at ? "opacity-70" : ""}`}
            >
              <div className={`w-2 h-2 rounded-full mt-1.5 flex-none ${n.read_at ? "bg-transparent" : "bg-[#B14A2A]"}`} />
              <div className="min-w-0 flex-1">
                <div className="text-[13px] font-semibold text-[#1F2A24] leading-snug">{n.title}</div>
                {n.body && <div className="text-[12px] text-[#5C7568] mt-0.5 leading-snug">{n.body}</div>}
                <div className="text-[10px] text-[#8A9992] mt-1 uppercase tracking-widest">{timeAgo(n.created_at)}</div>
              </div>
            </button>
          ))}
        </div>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

export default function Layout({ children }) {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const loc = useLocation();

  const NavLink = ({ to, icon: Icon, label, testId }) => {
    const active = loc.pathname === to || (to !== "/dashboard" && loc.pathname.startsWith(to));
    return (
      <Link
        to={to}
        data-testid={testId}
        className={`hidden md:inline-flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium transition-colors ${
          active ? "bg-[#0A4C36] text-white" : "text-[#47554D] hover:bg-[#F3F1EC]"
        }`}
      >
        <Icon size={16} strokeWidth={1.75} />
        {label}
      </Link>
    );
  };

  return (
    <div className="min-h-screen bg-[#FDFBF7]">
      <header className="sticky top-0 z-40 bg-white/80 backdrop-blur border-b border-[#EFECE5]">
        <div className="max-w-7xl mx-auto flex items-center justify-between px-6 md:px-8 py-4 gap-3">
          <Link to={user?.role === "staff" ? "/journey" : "/dashboard"} className="flex items-center gap-3" data-testid="brand-link">
            <div className="w-10 h-10 rounded-2xl bg-[#0A4C36] text-white flex items-center justify-center">
              <HealthCirclesLogo size={22} />
            </div>
            <div className="leading-tight">
              <div className="font-semibold text-[#0A4C36] text-lg tracking-tight" style={{ fontFamily: "Instrument Sans" }}>
                HealthCircles
              </div>
              <div className="text-[11px] text-[#8A9992] tracking-wider uppercase">Modern EMR</div>
            </div>
          </Link>

          <nav className="flex items-center gap-1 flex-wrap justify-center">
            {navForUser(user).map((n) => <NavLink key={n.to} {...n} />)}
          </nav>

          <div className="flex items-center gap-3">
            <BellDropdown />
            <NotificationCenter />
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button className="flex items-center gap-2 rounded-full pl-1 pr-3 py-1 hover:bg-[#F3F1EC]" data-testid="user-menu">
                  <div className="w-8 h-8 rounded-full bg-[#0A4C36] text-white flex items-center justify-center text-xs font-semibold">
                    {initials(user?.name || "Dr")}
                  </div>
                  <span className="hidden sm:block text-sm text-[#1F2A24] font-medium">
                    {user?.role === "staff" ? user?.name : `Dr. ${user?.name?.replace(/^Dr\.?\s*/i, "")}`}
                  </span>
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="rounded-2xl">
                <DropdownMenuLabel>{user?.email}</DropdownMenuLabel>
                <DropdownMenuSeparator />
                <DropdownMenuItem data-testid="menu-profile" onClick={() => nav("/profile")}>
                  <User size={14} className="mr-2" /> Profile
                </DropdownMenuItem>
                {user?.role !== "staff" && (
                  <>
                    <DropdownMenuItem data-testid="menu-availability" onClick={() => nav("/availability")}>
                      <Settings size={14} className="mr-2" /> Set availability
                    </DropdownMenuItem>
                    <DropdownMenuItem data-testid="menu-facilities" onClick={() => nav("/facilities")}>
                      <Building2 size={14} className="mr-2" /> My facilities
                    </DropdownMenuItem>
                    <DropdownMenuItem data-testid="menu-rx-groups" onClick={() => nav("/rx-groups")}>
                      <Pill size={14} className="mr-2" /> Rx Group
                    </DropdownMenuItem>
                  </>
                )}
                <DropdownMenuSeparator />
                <DropdownMenuItem data-testid="menu-logout" onClick={logout} className="text-red-600">
                  <LogOut size={14} className="mr-2" /> Log out
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-6 md:px-8 py-8">{children}</main>
    </div>
  );
}
